/**
 * (owner, Sep 25: "add bigger billboards that could have logos or images") The big billboards' geometry, shared by the generator
 * (world/generator.ts `pathBoards` / `roofBoards` place them, the posts block) and the renderer (engine/billboards.ts draws them).
 * A board is a face `w` × `d` tiles, its long side along `a` (radians), standing `lift` metres above `base` (the ground, or the
 * roof it is on). Pure: no canvas, no registry.
 */
import type { RoadClass } from '../content/types';
export interface PlacedBoard {
  /** the face's centre (world tiles) */
  x: number; y: number;
  /** the long side's direction (radians; 0 = along x) */
  a: number;
  w: number; d: number;
  /** the face's height above `base` (metres), and what it stands on (0 = the ground, a roof's height on a roof) */
  lift: number; base: number;
  /** the prop it is (index into gen.propList) */
  prop: number;
  /** on a roof: the building's key (no posts to the ground) */
  roof?: string;
  /** (Sep 25 follow-up) a roadside board: the class of the road it faces (a custom ad may ask for the motorways) */
  road?: RoadClass;
}

/** the face's corners, clockwise from the far-left one (world tiles) */
export function boardCorners(b: PlacedBoard): [number, number][] {
  const ux = Math.cos(b.a), uy = Math.sin(b.a), vx = -uy, vy = ux, hw = b.w / 2, hd = b.d / 2;
  return [[b.x - ux * hw - vx * hd, b.y - uy * hw - vy * hd], [b.x + ux * hw - vx * hd, b.y + uy * hw - vy * hd], [b.x + ux * hw + vx * hd, b.y + uy * hw + vy * hd], [b.x - ux * hw + vx * hd, b.y - uy * hw + vy * hd]];
}

/** where the posts stand (world points): two under a face up to 10 tiles wide, a third in the middle of a wider one, a tile in from each end */
export function boardPosts(b: PlacedBoard): [number, number][] {
  const ux = Math.cos(b.a), uy = Math.sin(b.a), k = b.w / 2 - 1.5;
  const at = b.w > 10.5 ? [-k, 0, k] : [-k, k];
  return at.map((t) => [b.x + ux * t, b.y + uy * t]);
}

/** every tile the face covers, grown by `grow` tiles all round (the generator's clearance test walks these) */
export function boardTiles(b: PlacedBoard, grow = 0): [number, number][] {
  const out: [number, number][] = [], seen = new Set<number>();
  const ux = Math.cos(b.a), uy = Math.sin(b.a), vx = -uy, vy = ux, hw = b.w / 2 + grow, hd = b.d / 2 + grow;
  for (let s = -hw; s <= hw + 1e-6; s += 0.5) for (let t = -hd; t <= hd + 1e-6; t += 0.5) {
    const x = Math.floor(b.x + ux * s + vx * t), y = Math.floor(b.y + uy * s + vy * t), k = (x + 32768) * 65536 + (y + 32768);
    if (seen.has(k)) continue; seen.add(k); out.push([x, y]);
  }
  return out;
}

/** a board's anchor for picking its ad (whole tiles, so every client and the server agree whatever the float noise) */
export function boardAnchor(b: { x: number; y: number }): [number, number] { return [Math.round(b.x), Math.round(b.y)]; }
